import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import * as Yup from "yup";

import ApiErrorsHelper from "~/admin/helpers/ApiErrorsHelper";
import useLoader from "~/hooks/useLoader";
import ValidationErrors from "~/admin/services/ValidationErrors";
import Button from "~/admin/components/Button";
import Input from "~/admin/components/Input";
import Label from "~/admin/components/Label";
import Modal from "~/admin/components/Modal";
import ValidationForm from "~/admin/components/ValidationForm";
import SubmitButton from "~/admin/components/SubmitButton";
import AdminsApi from "~/admin/apis/admins.api";

const validationSchema = Yup.object().shape({
  firstName: Yup.string()
    .max(255, ValidationErrors.isLength(255))
    .required(ValidationErrors.required),
  lastName: Yup.string()
    .max(255, ValidationErrors.isLength(255))
    .required(ValidationErrors.required),
  email: Yup.string()
    .email(ValidationErrors.isEMail)
    .max(255, ValidationErrors.isLength(255))
    .required(ValidationErrors.required),
  phoneNumber: Yup.string()
    .max(15, ValidationErrors.isLength(15))
    .required(ValidationErrors.required)
    .matches(/(0)[67][0-9]{8}/g, {
      message: ValidationErrors.validMobilePhoneNumber,
      excludeEmptyString: true,
    }),
  password: Yup.string()
    .matches(
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-.<>]).{8,}$/,
      ValidationErrors.insecuredPassword
    )
    .required(ValidationErrors.required),
});

const CreateAdminModal = forwardRef(({ onSubmitted }, ref) => {
  const [isOpened, setIsOpened] = useState(false);

  const form = useRef();
  const loader = useLoader();

  useImperativeHandle(ref, () => ({
    open() {
      setIsOpened(true);
    },
  }));

  const close = () => {
    setIsOpened(false);
  };

  const onSubmit = async (formData) => {
    loader.show();

    let error = null;
    await AdminsApi.create(formData).catch((e) => {
      error = e;
      return null;
    });

    loader.hide();

    if (error) {
      ApiErrorsHelper.manage(error, form);
    } else {
      setIsOpened(false);
      onSubmitted();
    }
  };

  return (
    <Modal visible={isOpened} toggle={close} size="lg">
      <Modal.Header toggle={close}>Créer un administrateur</Modal.Header>
      <ValidationForm
        ref={form}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
        initialValues={{
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          phoneNumber: "",
        }}
      >
        <>
          <Modal.Body>
            <Label html="firstName" required>
              Prénom
            </Label>
            <Input id="firstName" name="firstName" />

            <Label htmlFor="lastName" required>
              Nom
            </Label>
            <Input id="lastName" name="lastName" />
            <Label htmlFor="email" required>
              Email
            </Label>
            <Input id="email" name="email" />
            <Label htmlFor="password" required>
              Mot de passe
            </Label>
            <Input id="password" name="password" type="password" />
            <Label htmlFor="phoneNumber" required>
              Numéro de téléphone
            </Label>
            <Input id="phoneNumber" name="phoneNumber" />
          </Modal.Body>
          <Modal.Footer>
            <Button color="light" size="s" onClick={close}>
              Annuler
            </Button>
            <SubmitButton size="s" color="tertiary">
              Valider
            </SubmitButton>
          </Modal.Footer>
        </>
      </ValidationForm>
    </Modal>
  );
});

export default CreateAdminModal;
